/**
 * ProfilV2 — « Mon profil » (MVP lecture seule).
 *
 * Trois blocs :
 *   1. Identité  — avatar, nom, email, rôle canonique, périmètre clubs.
 *   2. Activité  — mes compteurs (tâches du jour, tâches ouvertes, demandes,
 *                  campagnes) + liste de mes tâches ouvertes. Tout cliquable.
 *   3. Compte    — déconnexion + rappel « édition via l'admin ».
 *
 * 100 % lecture seule sur ep:users : l'écriture de ep:users est réservée aux
 * admins côté serveur (server/lib/access.js). Aucune modification de profil ici.
 * Source : hooks read-only de useV1Data (mêmes données que le Dashboard).
 */
import React, { useMemo } from "react";
import { useTasks, useRequests, useCampagnes, useClubs, filterTodayTasks } from "../hooks/useV1Data";
import { roleOf, ROLE_LABELS, scopeClubs, isAdmin as isAdminUser } from "../lib/roles";
import { useAuth } from "../../auth/AuthContext";
import { logoutUser } from "../../auth/authService";
import {
  IconCheckSquare, IconInbox, IconMegaphone, IconMail,
  IconUsers, IconLogOut, IconChevronRight,
} from "../icons";

const ACCENT = "#0071CD";

const ROLE_COLOR = {
  admin:     "#7C3AED",
  comm:      "#0071CD",
  directeur: "#0E9F6E",
  ja:        "#D97706",
  guest:     "#64748B",
};

/* ── Helpers ─────────────────────────────────────────────── */

function nameOf(u, fallback) {
  if (!u) return fallback || "Utilisateur";
  return (
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.name || u.displayName || u.email || fallback || "Utilisateur"
  );
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0][0] || "?").toUpperCase();
}

function shortDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const diff = Math.round((dt - Date.now()) / 86400000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Demain";
  if (diff === -1) return "Hier";
  return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const CLOSED = new Set([
  "clôturée", "cloturee", "closed", "refusée", "refused", "rejected",
  "done", "completed", "terminé", "terminée", "termine", "terminee",
  "archivé", "archivee", "annulée", "annulee",
]);

/** `assignedTo` tolérant : tableau d'ids mixtes, id simple, ou champ assigneeId. */
function isAssignedToMe(entity, me) {
  if (!entity || !me) return false;
  const m = String(me);
  const at = entity.assignedTo;
  if (Array.isArray(at) && at.some((x) => String(x) === m)) return true;
  if (at != null && !Array.isArray(at) && String(at) === m) return true;
  if (entity.assigneeId != null && String(entity.assigneeId) === m) return true;
  return false;
}

function taskPriority(t) {
  const p = String(t.priority || t.priorite || t.importance || "").toLowerCase();
  if (p.includes("haut") || p.includes("urgent") || p.includes("high") || p.includes("élev") || p.includes("elev")) return { variant: "red", label: "Haute" };
  if (p.includes("moy") || p.includes("medium") || p.includes("normal")) return { variant: "yellow", label: "Moyenne" };
  if (p.includes("bas") || p.includes("low") || p.includes("faible")) return { variant: "gray", label: "Basse" };
  return null;
}

function Badge({ variant = "gray", children }) {
  return <span className={`v2-badge v2-badge--${variant}`}>{children}</span>;
}

function clubNameResolver(clubs) {
  const m = {};
  (clubs || []).forEach((c) => { if (c && c.id != null) m[String(c.id)] = c.name || c.nom || null; });
  return (v) => {
    if (v == null || v === "") return null;
    const s = String(v);
    if (m[s]) return m[s];
    return /^\d{6,}$/.test(s) ? null : s;
  };
}

/* ── Compteur ────────────────────────────────────────────── */
function StatCard({ icon: Icon, value, label, color, onClick }) {
  return (
    <div
      className={`v2-counter v2-counter--${color}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
    >
      <div className="v2-counter__top">
        <div className="v2-counter__icon"><Icon size={20} /></div>
        <div className="v2-counter__arrow"><IconChevronRight size={14} /></div>
      </div>
      <div>
        <div className="v2-counter__value">{value}</div>
        <div className="v2-counter__label">{label}</div>
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────── */
export default function ProfilV2({ appId, currentUser, onNavigate }) {
  const { firebaseUser } = useAuth();
  const { tasks,     loading: loadingTasks } = useTasks();
  const { requests,  loading: loadingReq   } = useRequests();
  const { campagnes, loading: loadingCamp  } = useCampagnes();
  const { clubs } = useClubs();

  const clubNameOf = useMemo(() => clubNameResolver(clubs), [clubs]);

  const displayName = nameOf(
    currentUser,
    firebaseUser?.displayName || firebaseUser?.email?.split("@")[0]
  );
  const email = currentUser?.email || firebaseUser?.email || "—";
  const role = roleOf(currentUser);
  const admin = isAdminUser(currentUser);
  const myClubIds = scopeClubs(currentUser);
  const myClubNames = myClubIds.map((id) => clubNameOf(id)).filter(Boolean);

  // ── Scopes « moi » ──
  const myTodayTasks = useMemo(() => filterTodayTasks(tasks, appId), [tasks, appId]);

  const myOpenTasks = useMemo(() => {
    const list = (tasks || []).filter((t) =>
      t && t.done !== true &&
      !CLOSED.has(String(t.status || t.statut || "").toLowerCase()) &&
      isAssignedToMe(t, appId)
    );
    list.sort((a, b) => {
      const da = a.dueDate || a.deadline || a.date;
      const db = b.dueDate || b.deadline || b.date;
      return (da ? new Date(da).getTime() : Infinity) - (db ? new Date(db).getTime() : Infinity);
    });
    return list;
  }, [tasks, appId]);

  const myRequests = useMemo(() => (requests || []).filter((r) =>
    r && !CLOSED.has(String(r.status || r.statut || "").toLowerCase()) && isAssignedToMe(r, appId)
  ), [requests, appId]);

  const myCampagnes = useMemo(() => (campagnes || []).filter((c) =>
    c && appId != null && String(c.owner) === String(appId)
  ), [campagnes, appId]);

  const dash = (v, loading) => (loading ? "—" : v);

  return (
    <div style={{ padding: "24px 28px", maxWidth: 960, margin: "0 auto" }}>

      {/* ── 1. Identité ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
        background: "#fff", border: "1px solid #EEF2F7", borderRadius: 14,
        padding: "22px 24px", marginBottom: 20,
      }}>
        <span
          aria-hidden="true"
          style={{
            width: 64, height: 64, borderRadius: "50%", flexShrink: 0,
            background: "#FFB600", color: "#1a1a1c",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, fontWeight: 800,
          }}
        >{initials(displayName)}</span>

        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text, #1a1a1c)", lineHeight: 1.2 }}>
            {displayName}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 5, fontSize: 13.5, color: "#64748B" }}>
            <IconMail size={14} />
            <span>{email}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
            <span style={{
              fontSize: 12, fontWeight: 700, padding: "4px 11px", borderRadius: 999,
              color: ROLE_COLOR[role], background: `${ROLE_COLOR[role]}14`,
            }}>{ROLE_LABELS[role]}</span>

            {admin ? (
              <span style={{ fontSize: 12, color: "#64748B", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <IconUsers size={13} /> Tous les clubs
              </span>
            ) : myClubNames.length ? (
              myClubNames.map((n, i) => (
                <span key={i} style={{ fontSize: 11.5, color: "#475569", background: "#F1F5F9", borderRadius: 6, padding: "3px 9px" }}>{n}</span>
              ))
            ) : (
              <span style={{ fontSize: 11.5, color: "#CBD5E1" }}>Aucun club rattaché</span>
            )}
          </div>
        </div>
      </div>

      {/* ── 2. Mon activité ── */}
      <div style={{ fontSize: 13, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".04em", margin: "0 2px 12px" }}>
        Mon activité
      </div>

      <div className="v2-dash-counters" style={{ marginBottom: 20 }}>
        <StatCard
          icon={IconCheckSquare}
          value={dash(myTodayTasks.length, loadingTasks)}
          label="À traiter aujourd'hui"
          color="yellow"
          onClick={() => onNavigate?.("projets")}
        />
        <StatCard
          icon={IconCheckSquare}
          value={dash(myOpenTasks.length, loadingTasks)}
          label="Mes tâches ouvertes"
          color="blue"
          onClick={() => onNavigate?.("projets")}
        />
        <StatCard
          icon={IconInbox}
          value={dash(myRequests.length, loadingReq)}
          label="Demandes qui me sont assignées"
          color="orange"
          onClick={() => onNavigate?.("demandes")}
        />
        <StatCard
          icon={IconMegaphone}
          value={dash(myCampagnes.length, loadingCamp)}
          label="Mes campagnes"
          color="gray"
          onClick={() => onNavigate?.("campagnes")}
        />
      </div>

      {/* Liste : mes tâches ouvertes */}
      <div className="v2-block" style={{ marginBottom: 20 }}>
        <div className="v2-block-header">
          <span className="v2-block-header__label">Mes tâches ouvertes</span>
          {!loadingTasks && myOpenTasks.length > 0 && (
            <span className="v2-block-header__count">{myOpenTasks.length}</span>
          )}
          <span className="v2-block-header__link" onClick={() => onNavigate?.("projets")}>
            Voir tout <IconChevronRight size={13} />
          </span>
        </div>
        <div className="v2-block-divider" />

        {loadingTasks ? (
          <div style={{ padding: "14px 18px" }}>
            <div className="v2-skel" style={{ height: 12, borderRadius: 4, marginBottom: 6 }} />
            <div className="v2-skel" style={{ height: 12, width: "80%", borderRadius: 4, marginBottom: 6 }} />
            <div className="v2-skel" style={{ height: 12, width: "60%", borderRadius: 4 }} />
          </div>
        ) : myOpenTasks.length === 0 ? (
          <div className="v2-empty" style={{ padding: "28px 18px" }}>
            <div className="v2-empty__icon"><IconCheckSquare size={28} /></div>
            <div className="v2-empty__title">Aucune tâche qui vous est assignée</div>
          </div>
        ) : (
          myOpenTasks.slice(0, 6).map((t, i) => {
            const due = shortDate(t.dueDate || t.deadline || t.date);
            const clubLabel = clubNameOf(t.club || (Array.isArray(t.clubs) && t.clubs[0]) || null);
            const prio = taskPriority(t);
            return (
              <div key={t.id || i} className="v2-recent-row" onClick={() => onNavigate?.("projets")}>
                <div className="v2-recent-row__type">
                  <span className="v2-task-check" aria-hidden="true" />
                </div>
                <div className="v2-recent-row__body">
                  <div className="v2-recent-row__title">{t.title || t.titre || t.name || t.label || "Tâche"}</div>
                  <div className="v2-recent-row__meta">
                    {clubLabel && <><span>{clubLabel}</span><div className="v2-recent-row__meta-dot" /></>}
                    {due && <span>{due}</span>}
                  </div>
                </div>
                <div className="v2-recent-row__right">
                  {prio && <Badge variant={prio.variant}>{prio.label}</Badge>}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── 3. Compte ── */}
      <div style={{ fontSize: 13, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".04em", margin: "0 2px 12px" }}>
        Compte
      </div>

      <div style={{
        background: "#fff", border: "1px solid #EEF2F7", borderRadius: 14,
        padding: "18px 22px", display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5, maxWidth: 520 }}>
          {admin
            ? "Vous gérez les membres, rôles et clubs depuis la page Équipe."
            : "Pour modifier votre nom, votre rôle ou vos clubs, contactez le responsable communication."}
        </div>
        <button
          onClick={() => { logoutUser().catch(() => {}); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "9px 16px", borderRadius: 9, border: "1px solid #E2E8F0",
            background: "#fff", color: "#B23B3B", fontSize: 13.5, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
          }}
        >
          <IconLogOut size={15} /> Se déconnecter
        </button>
      </div>
    </div>
  );
}
